import React from 'react';
import { Package, Settings, Check, Sparkles } from 'lucide-react';
import { PlatformCard } from '../../../components/common';
import InfoTooltip from '../../../components/common/InfoTooltip';

const PlatformStep = ({ quoteData, setQuoteData, pricing }) => {
  const isProspect = quoteData.customerType === 'prospect';

  const deployments = [
    {
      id: 'On-Premise',
      name: 'On-Premise',
      ...pricing.deployment['On-Premise']
    },
    {
      id: 'Private Hosting',
      name: 'Private Hosting',
      ...pricing.deployment['Private Hosting']
    },
    {
      id: 'Public Cloud',
      name: 'Public Cloud (SaaS)',
      ...pricing.deployment['Public Cloud']
    }
  ];

  // Combined features for SplashBIxAI (Prospect bundle)
  const combinedFeatures = [
    'Multi-Source Integration',
    'AI-Powered Insights',
    'Advanced Analytics',
    'Predictive Analytics',
    'Natural Language Queries',
    'Automated Insights'
  ];

  // Handle prospect platform toggle (combined SplashBIxAI)
  const handleProspectPlatformToggle = () => {
    const isSelected = quoteData.hasSplashBI && quoteData.hasSplashAI;
    setQuoteData({
      ...quoteData,
      hasSplashBI: !isSelected,
      hasSplashAI: !isSelected
    });
  };

  const hasPlatformSelected = isProspect
    ? (quoteData.hasSplashBI && quoteData.hasSplashAI)
    : quoteData.hasSplashBI;

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">Platform</h2>
      <p className="text-slate-600 mb-8">Choose your platform and deployment model</p>

      <div className="space-y-8">
        {/* Platform Selection */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Package size={18} className="text-blue-600" />
            Platform Foundation
            <InfoTooltip
              text={isProspect
                ? "SplashBIxAI combines our core analytics platform with AI-powered predictive analytics in one bundled offering for new customers."
                : "SplashBI is the core analytics platform required for all deals. SplashAI adds AI-powered predictive analytics and natural language queries as an optional add-on."
              }
              position="right"
            />
          </h3>

          <div className="space-y-4">
            {isProspect ? (
              /* Prospect: Combined SplashBIxAI Platform */
              <div
                onClick={handleProspectPlatformToggle}
                className={`p-6 rounded-xl border-2 cursor-pointer transition-all ${
                  hasPlatformSelected
                    ? 'border-purple-500 bg-gradient-to-r from-blue-50 to-purple-50'
                    : 'border-slate-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles size={20} className="text-purple-600" />
                      <h4 className="text-lg font-bold text-slate-800">SplashBIxAI Platform</h4>
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        New Customer Bundle
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mb-3">
                      Complete analytics platform with AI-powered insights for HCM, Finance, CRM, SCM solutions
                    </p>
                    <p className="text-xs text-slate-400 mb-4">SKU: {pricing.platform.SplashAI.sku}</p>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {combinedFeatures.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                          <Check size={14} className={idx < 3 ? 'text-blue-500' : 'text-purple-500'} />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-2xl font-bold text-purple-600">$100K</div>
                    <div className="text-xs text-slate-500">/year</div>
                    {hasPlatformSelected && (
                      <div className="mt-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center mx-auto">
                        <Check size={14} className="text-white" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* Existing Customer: Separate SplashBI + SplashAI */
              <>
                <PlatformCard
                  title="SplashBI Platform"
                  description="Core platform for HCM, Finance, CRM, SCM solutions"
                  price={`$${(pricing.platform.SplashBI.price / 1000).toFixed(0)}K`}
                  sku={pricing.platform.SplashBI.sku}
                  selected={quoteData.hasSplashBI}
                  onToggle={() => setQuoteData({...quoteData, hasSplashBI: !quoteData.hasSplashBI})}
                  features={['Multi-Source Integration', 'AI-Powered Insights', 'Advanced Analytics']}
                  color="blue"
                />

                {quoteData.hasSplashBI && (
                  <PlatformCard
                    title="SplashAI Platform"
                    description="Advanced AI-powered analytics and predictions"
                    price={`+$${(pricing.platform.SplashAI.price / 1000).toFixed(0)}K`}
                    sku={pricing.platform.SplashAI.sku}
                    selected={quoteData.hasSplashAI}
                    onToggle={() => setQuoteData({...quoteData, hasSplashAI: !quoteData.hasSplashAI})}
                    features={['Predictive Analytics', 'Natural Language Queries', 'Automated Insights']}
                    color="purple"
                    addon
                  />
                )}
              </>
            )}
          </div>
        </div>

        {/* Deployment */}
        {hasPlatformSelected && (
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Settings size={18} className="text-blue-600" />
              Deployment Model
              <InfoTooltip
                text="On-Premise: Customer hosts on their servers. Private Hosting: SplashBI hosts dedicated infrastructure. Public Cloud (SaaS): Shared multi-tenant cloud with lowest cost."
                position="right"
              />
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {deployments.map(deploy => (
                <button
                  key={deploy.id}
                  onClick={() => setQuoteData({...quoteData, deployment: deploy.id})}
                  className={`p-5 rounded-xl border-2 transition-all text-left ${
                    quoteData.deployment === deploy.id 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <h4 className="font-semibold text-slate-800 mb-1">{deploy.name}</h4>
                  <p className="text-xs text-slate-400 mb-2">SKU: {deploy.sku}</p>
                  <p className="text-sm text-slate-600 mb-3">{deploy.desc}</p>
                  <div className="text-lg font-bold text-blue-600">
                    {deploy.price > 0 ? `$${(deploy.price / 1000).toFixed(0)}K/year` : 'Included'}
                  </div>
                  {quoteData.deployment === deploy.id && (
                    <div className="mt-3 flex items-center justify-center gap-2 text-blue-600">
                      <Check size={16} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformStep;
